import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: any) => void;
  mode: 'create' | 'edit';
  user?: any;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  mode, 
  user 
}) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    role: user?.role || 'dentist'
  });

  const roles = [
    { value: 'admin', label: 'Administrador', description: 'Acceso completo al sistema' },
    { value: 'dentist', label: 'Dentista', description: 'Gestión de pacientes y citas' },
    { value: 'assistant', label: 'Asistente', description: 'Apoyo en consultas y procedimientos' },
    { value: 'receptionist', label: 'Recepcionista', description: 'Gestión de citas y recepción' },
    { value: 'patient', label: 'Paciente', description: 'Acceso a citas propias únicamente' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {mode === 'create' ? 'Registrar Usuario del Equipo' : `Editar Usuario - ${user?.firstName} ${user?.lastName}`}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  Nombre
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  required
                  disabled={mode === 'edit'}
                  value={formData.firstName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dental-500 focus:ring-dental-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Apellidos
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  required
                  disabled={mode === 'edit'}
                  value={formData.lastName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dental-500 focus:ring-dental-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                disabled={mode === 'edit'}
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dental-500 focus:ring-dental-500 disabled:bg-gray-100"
              />
            </div>

            {mode === 'create' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Contraseña Temporal
                </label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dental-500 focus:ring-dental-500"
                  placeholder="Mínimo 6 caracteres"
                />
                <p className="mt-1 text-xs text-gray-500">
                  El usuario podrá cambiar esta contraseña después del primer login
                </p>
              </div>
            )}

            {mode === 'edit' && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  <strong>Modo edición:</strong> Solo se puede cambiar el rol del usuario. 
                  Los datos personales no son modificables.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                Rol del Usuario
              </label>
              <div className="space-y-2">
                {roles.map((roleOption) => (
                  <label key={roleOption.value} className="flex items-start">
                    <input
                      type="radio"
                      name="role"
                      value={roleOption.value}
                      checked={formData.role === roleOption.value}
                      onChange={handleChange}
                      className="mt-1 mr-3 text-dental-600 focus:ring-dental-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {roleOption.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {roleOption.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-dental-600 border border-transparent rounded-md hover:bg-dental-700"
              >
                {mode === 'create' ? 'Registrar Usuario' : 'Cambiar Rol'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagementModal;